<template>
  <div class="chess">
    <chess-desktop
      v-if="$vuetify.display.mdAndUp"
      ref="chessDesktopRef"
      v-bind="props"
      :chess-dates-available-from="props.chessDatesAvailableFrom"
      @select="emit('select', $event)"
    >
      <template v-if="slots.tooltip" #tooltip="{ item }">
        <slot name="tooltip" :item="item" />
      </template>
    </chess-desktop>
    <chess-mobile
      v-else
      ref="chessMobileRef"
      v-bind="props"
      :chess-dates-available-from="props.chessDatesAvailableFrom"
      @select="emit('select', $event)"
    />

    <cover-loader :shown="props.disableLoading" />
  </div>
</template>

<script setup lang="ts" generic="T">
import { type Dayjs } from 'dayjs';
import { ref } from 'vue';
import type {
  ChessDateCellType,
  ChessDesktopComponent,
  ChessItemType,
  ChessMobileComponent,
  ChessTableRowType,
} from '@/core/components/interface/Chess/Chess.type';
import type { RangeOfDateOnly } from '@/graphql/generated';
import ChessDesktop from '@/core/components/interface/Chess/ChessDesktop.vue';
import ChessMobile from '@/core/components/interface/Chess/ChessMobile.vue';
import CoverLoader from '@/core/components/interface/CoverLoader.vue';

import { getIsoDateFormat } from '@/core/utils/date';

const props = defineProps<{
  period?: RangeOfDateOnly;
  items: ChessItemType<T>[];
  isEdit?: boolean;
  accordion?: boolean;
  accordionMultiple?: boolean;
  loading?: boolean;
  disableLoading?: boolean;
  chessDatesAvailableFrom?: string; // Date in format YYYY-MM-DD
  dialogTitleType?: 'full' | 'row';
  showPlaceWithTypeSelect?: boolean;
}>();

const emit = defineEmits<{
  (event: 'select', value: ChessDateCellType<T>[]): void;
}>();

const slots = defineSlots<{
  tooltip?: (props: { item: ChessTableRowType<T> }) => void;
}>();

const chessDesktopRef = ref<ChessDesktopComponent>();
const chessMobileRef = ref<ChessMobileComponent>();

const scrollTo = (date: string | Date | Dayjs) => {
  const formattedDate = getIsoDateFormat(date);

  if (chessDesktopRef.value) {
    chessDesktopRef.value.scrollTo(formattedDate);
  } else if (chessMobileRef.value) {
    chessMobileRef.value.scrollTo(formattedDate);
  }
};

defineExpose({
  unselectAllCells: () => {
    if (chessDesktopRef.value) {
      chessDesktopRef.value.unselectAllCells();
    }
  },
  scrollTo,
});
</script>

<style scoped lang="scss">
.chess {
  position: relative;
}
</style>
